import React, { useEffect, useMemo, useState } from 'react';
import {
    Form,
    Card,
    Col,
    Row,
    Typography,
    Button,
    message,
    InputNumber,
    Breadcrumb,
    Drawer,
    Empty,
    Input,
    Divider,
    Modal,
    Tag,
    Spin,
} from 'antd';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';

import { Price, ThreeJSThumbnail } from '@partsbadger/library';
import QuickTurnPartStore from 'stores/QuickTurnPartStore';
import PortalLoading from 'components/PortalLoading';
import Menu from '../../../components/QuickTurn/Menu';

import { QuickTurnSalesOrderLineItem } from '@partsbadger/types';

import ThreeJSCanvas from 'components/QuickTurn/ThreeJSCanvas';
import PartSpecificationForm from 'components/QuickTurn/PartSpecificationForm';
import UserStore from 'stores/UserStore';

import AuthLayout from 'components/AuthLayout';
import CouponInput from 'components/QuickTurn/CouponInput';
import QuickTurnProgramCapacityAlert from '../../../components/QuickTurn/QuickTurnProgramCapacityAlert';

const Cart = () => {
    const { orderToken, orderData } = QuickTurnPartStore;

    const [editPartDrawerVisible, setEditPartDrawerVisible] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState<boolean>(false);

    const history = useHistory();

    useEffect(() => {
        if (orderToken) {
            QuickTurnPartStore.getSalesOrderById(orderToken);
        }
    }, []);

    const totalLineItems = orderData?.line_items?.reduce((acc, curr) => acc + curr.part.quantity, 0);

    const handleUpdateQuantity = async (item: QuickTurnSalesOrderLineItem, quantity: number) => {
        try {
            if (orderToken) {
                await QuickTurnPartStore.updateSalesOrderItem({
                    line_item: item.id,
                    quantity: quantity,
                });
            }
        } catch (err) {
            message.error('Error updating quantity');
        }
    };

    const handleEdit = async (id: string) => {
        try {
            // QuickTurnPartStore.setPartToken(id);
            // history.push('/quick-turn/design');
            QuickTurnPartStore.setPartToken(id);
            await QuickTurnPartStore.getPartByID(id);

            setEditPartDrawerVisible(true);
        } catch (err) {
            message.error('Error trying to edit');
        }
    };

    const handleSubmitUpdatePart = async () => {
        try {
            await QuickTurnPartStore.updatePartById(QuickTurnPartStore.partToken, undefined, true);

            setEditPartDrawerVisible(false);

            await QuickTurnPartStore.getSalesOrderById(QuickTurnPartStore.orderToken);

            QuickTurnPartStore.setPartToken(null);
        } catch (error) {
            message.error('Error updating part');
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await QuickTurnPartStore.removeItemFromOrder(id);
        } catch (err) {
            message.error('Error trying to delete');
        }
    };

    const associateSalesOrderWithUserId = async () => {
        if (!QuickTurnPartStore?.orderData?.user_id) {
            await QuickTurnPartStore.updateSalesOrder(QuickTurnPartStore?.orderToken, {});
        }
    };

    const handleLoginSuccess = async () => {
        await associateSalesOrderWithUserId();

        setShowAuthModal(false);
        history.push('/quick-turn/cart/checkout/');
    };

    return (
        <>
            <Menu title="Cart" />

            <div className="container">
                <>
                    <Breadcrumb>
                        <Breadcrumb.Item>
                            <Link to="/quick-turn/design">QuickTurn</Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>Cart</Breadcrumb.Item>
                    </Breadcrumb>
                    <QuickTurnProgramCapacityAlert />
                    {!orderToken || !orderData?.line_items?.length ? (
                        <>
                            <Empty image={null} description={<span className="font-size-lg">Your Cart is Empty</span>}>
                                <Link to="/quick-turn/design" className="ant-btn ant-btn-primary">
                                    Design a Part
                                </Link>
                            </Empty>
                        </>
                    ) : (
                        <>
                            <Row gutter={[16, 0]}>
                                <Col xl={16} lg={14} span={24}>
                                    {orderData?.line_items?.map(item => {
                                        // Generate a string that changes on every update to use as a trigger for
                                        // re-rendering the ThreeJS thumbnail

                                        const d = new Date();
                                        const seed = String(d.getTime());

                                        return (
                                            <Card
                                                className="shadow-sm mb-2"
                                                key={item.id}
                                                bodyStyle={{ padding: '1rem' }}
                                            >
                                                <div className="d-flex flex-xl-row flex-column justify-content-between w-100">
                                                    <div className="d-flex flex-row">
                                                        <div className="mr-3">
                                                            {item?.part?.image_file?.file ? (
                                                                <img
                                                                    style={{
                                                                        transform: 'scaleX(-1)',
                                                                        filter: 'brightness(114%)',
                                                                        width: '125px',
                                                                    }}
                                                                    src={item.part.image_file.file}
                                                                />
                                                            ) : (
                                                                <div
                                                                    style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'center',
                                                                        alignItems: 'center',
                                                                        height: '125px',
                                                                    }}
                                                                >
                                                                    <Spin />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="d-flex flex-column justify-content-between">
                                                            <div className="d-flex flex-column">
                                                                <span className="font-size-md font-weight-semibold mb-2">
                                                                    {item.part.name}
                                                                </span>
                                                                <span>
                                                                    <span className="font-weight-semibold">
                                                                        Material:
                                                                    </span>{' '}
                                                                    <span>{`${item.part.material.type} ${item.part.material.alloy}`}</span>
                                                                </span>
                                                                <span>
                                                                    <span className="font-weight-semibold">
                                                                        Overall Length:
                                                                    </span>{' '}
                                                                    <span>{`${item.part.overallLength}"`}</span>
                                                                </span>
                                                                {/* <span>
                                                                        <span className="font-weight-semibold">
                                                                            Outer Diameter:
                                                                        </span>
                                                                        <span>{`${item.part.overallLength}"`}</span>
                                                                    </span>
                                                                    <span className="font-weight-semibold">
                                                                        Chamfer Size: {`${item.part.overallLength}"`}
                                                                    </span>
                                                                    <span className="font-weight-semibold">
                                                                        Inner diameter: {`${item.part.overallLength}"`}
                                                                    </span> */}
                                                            </div>
                                                            <div
                                                                className="d-flex flex-row align-items-center"
                                                                style={{ columnGap: '6px' }}
                                                            >
                                                                <span
                                                                    className="link underline"
                                                                    onClick={() => {
                                                                        handleEdit(item.part.id);
                                                                    }}
                                                                >
                                                                    Edit
                                                                </span>
                                                                <Divider type="vertical" className="m-0 p-0" />
                                                                <span
                                                                    className="link underline"
                                                                    onClick={() => {
                                                                        handleDelete(item.id);
                                                                    }}
                                                                >
                                                                    Remove
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="d-flex flex-xl-column-reverse flex-row text-right justify-content-between align-items-end">
                                                        <div className="quantity-selector d-flex flex-row align-items-center justify-content-end">
                                                            <div className="d-flex flex-row align-items-center">
                                                                <label
                                                                    htmlFor="quantity-input"
                                                                    className="ant-form-item-label mr-2"
                                                                >
                                                                    Qty
                                                                </label>
                                                                <div className="button-group">
                                                                    <Button
                                                                        size="small"
                                                                        className="minus-btn"
                                                                        icon={<MinusOutlined />}
                                                                        onClick={() =>
                                                                            handleUpdateQuantity(
                                                                                item,
                                                                                item.part.quantity - 1
                                                                            )
                                                                        }
                                                                        disabled={item.part.quantity === 1}
                                                                    />
                                                                    <Form.Item noStyle>
                                                                        <InputNumber
                                                                            id="quantity-input"
                                                                            min={1}
                                                                            max={1000}
                                                                            step={1}
                                                                            controls={false}
                                                                            size="small"
                                                                            value={item.part.quantity}
                                                                            onPressEnter={(e: any) => {
                                                                                if (Number(e.target.value) <= 1000) {
                                                                                    handleUpdateQuantity(
                                                                                        item,
                                                                                        Number(e.target.value)
                                                                                    );
                                                                                }
                                                                            }}
                                                                            onBlur={(e: any) => {
                                                                                if (Number(e.target.value) <= 1000) {
                                                                                    handleUpdateQuantity(
                                                                                        item,
                                                                                        Number(e.target.value)
                                                                                    );
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Form.Item>
                                                                    <Button
                                                                        size="small"
                                                                        className="plus-btn"
                                                                        icon={<PlusOutlined />}
                                                                        onClick={() =>
                                                                            handleUpdateQuantity(
                                                                                item,
                                                                                item.part.quantity + 1
                                                                            )
                                                                        }
                                                                        disabled={item.part.quantity === 1000}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="d-flex flex-column justify-content-center ml-3 mb-xl-3"
                                                            style={{ minWidth: '100px' }}
                                                        >
                                                            <span
                                                                style={{
                                                                    fontSize: '1rem',
                                                                    fontWeight: 400,
                                                                    paddingLeft: '0.5rem',
                                                                }}
                                                            >
                                                                <Price value={item.unit_price} /> ea.
                                                            </span>
                                                            <span
                                                                style={{
                                                                    fontSize: '1.3rem',
                                                                    fontWeight: 500,
                                                                    paddingLeft: '0.5rem',
                                                                }}
                                                            >
                                                                <Price value={item.total_price} />
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Card>
                                        );
                                    })}
                                </Col>
                                <Col xl={8} lg={10} span={24}>
                                    <Card
                                        title={
                                            <>
                                                <div className="d-flex flex-row justify-content-between">
                                                    <span className="font-size-lg font-weight-normal">
                                                        Subtotal ({totalLineItems}{' '}
                                                        {totalLineItems === 1 ? 'Item' : 'Items'}
                                                        ):
                                                    </span>
                                                    <div>
                                                        {orderData?.coupon?.code ? (
                                                            <span
                                                                className="font-weight-normal text-muted mr-2"
                                                                style={{
                                                                    fontSize: '1.2rem',
                                                                    textDecoration: 'line-through',
                                                                }}
                                                            >
                                                                <Price value={orderData?.subtotal} />
                                                            </span>
                                                        ) : null}
                                                        <span className="font-size-lg font-weight-semibold">
                                                            <Price value={orderData?.subtotal_after_discount} />
                                                        </span>
                                                    </div>
                                                </div>
                                                {orderData?.coupon?.code ? (
                                                    <div className="d-flex flex-row justify-content-between align-items-baseline mb-3">
                                                        <span className="font-size-md font-weight-normal">
                                                            Discount
                                                        </span>
                                                        <span className="font-size-md font-weight-normal">
                                                            -<Price value={Number(orderData?.discount)} />
                                                        </span>
                                                    </div>
                                                ) : null}
                                            </>
                                        }
                                        className="shadow-sm mb-2"
                                    >
                                        <div className="d-flex flex-row justify-content-between align-items-baseline mb-3">
                                            <span className="font-size-md font-weight-med">Estimated Lead Time</span>
                                            <Tag color="green" className="m-0">
                                                <span className="font-size-md font-weight-semibold">
                                                    7 Business Days
                                                </span>
                                            </Tag>
                                        </div>
                                        <Button
                                            id="quickturn-continue-to-checkout"
                                            type="primary"
                                            size="large"
                                            block
                                            disabled={!QuickTurnPartStore.isPortalEnabled}
                                            onClick={() => {
                                                if (!UserStore.isAuthenticated) {
                                                    return setShowAuthModal(true);
                                                } else {
                                                    history.push('/quick-turn/cart/checkout/');
                                                }
                                            }}
                                        >
                                            Proceed to Checkout
                                        </Button>
                                    </Card>
                                    <Card bodyStyle={{ padding: '0 0.5rem' }} className="shadow-sm">
                                        <CouponInput />
                                    </Card>
                                </Col>
                            </Row>
                            <Drawer
                                open={editPartDrawerVisible}
                                onClose={() => {
                                    setEditPartDrawerVisible(false);
                                    QuickTurnPartStore.setPartToken(null);
                                }}
                                width={'auto'}
                                bodyStyle={{ padding: 0 }}
                                closable={false}
                                drawerStyle={{ width: '95vw', maxWidth: '480px' }}
                                footer={
                                    <div className="d-flex flex-row" style={{ columnGap: '0.5rem' }}>
                                        <Button
                                            type="default"
                                            className="w-100"
                                            onClick={() => setEditPartDrawerVisible(false)}
                                        >
                                            Cancel
                                        </Button>
                                        <Button type="primary" className="w-100" onClick={handleSubmitUpdatePart}>
                                            Update
                                        </Button>
                                    </div>
                                }
                                destroyOnClose
                            >
                                <>
                                    <ThreeJSCanvas
                                        showGrid={false}
                                        showViewCube={false}
                                        showDimensions={false}
                                        borderRadius="0"
                                    />
                                    <div className="d-flex flex-column text-right pr-4 mb-0">
                                        <span className="m-0 font-size-xxl font-weight-semibold plus-jakarta-sans">
                                            <Price value={QuickTurnPartStore.price} />
                                            <span className="font-weight-light">/pc</span>
                                        </span>
                                    </div>

                                    <PartSpecificationForm
                                        mode="preview"
                                        showNameInput={true}
                                        showInterestIndicators={false}
                                    />
                                </>
                            </Drawer>

                            <Modal
                                open={showAuthModal}
                                footer={null}
                                onCancel={() => setShowAuthModal(false)}
                                closable={true}
                                maskClosable={true}
                                width="auto"
                                centered
                            >
                                <AuthLayout
                                    onLoginSuccess={async () => {
                                        handleLoginSuccess();
                                    }}
                                    onRegisterSuccess={async () => {
                                        handleLoginSuccess();
                                    }}
                                />
                            </Modal>
                        </>
                    )}
                </>
            </div>
        </>
    );
};

export default observer(Cart);
